import { z } from 'zod';

/**
 * Meta WhatsApp Business Cloud API payloads: inbound webhook (messages + statuses), webhook
 * verification query, outbound send request/response bodies and the Graph API error envelope.
 */

export const WA_MESSAGE_TYPES = [
  'text',
  'image',
  'document',
  'audio',
  'video',
  'sticker',
  'location',
  'contacts',
  'interactive',
  'button',
  'reaction',
  'order',
  'system',
  'unknown',
  'unsupported',
] as const;
export type WaMessageType = (typeof WA_MESSAGE_TYPES)[number];

export const WA_MEDIA_TYPES = ['image', 'document', 'audio', 'video', 'sticker'] as const;

export const WA_STATUSES = ['sent', 'delivered', 'read', 'failed'] as const;
export type WaStatus = (typeof WA_STATUSES)[number];
export const waStatusValueSchema = z.enum(WA_STATUSES);

/** Body placeholder stored for inbound media without a caption. */
export const WA_MEDIA_PLACEHOLDER = '[media]';

/** Digits-only WhatsApp id as Meta sends it in `from`, `wa_id`, `recipient_id` and expects in `to`. */
export const waDigitsSchema = z.string().regex(/^[0-9]{6,15}$/, 'WhatsApp id must be 6-15 digits');
/** +E.164 as stored in wa_conversations.phone (DB check `^\+[0-9]{6,15}$`). */
export const waE164Schema = z.string().regex(/^\+[1-9][0-9]{5,14}$/, 'Phone must be +E.164');

/**
 * Digits → +E.164 with the prototype's `normalizeWhatsappPhone` rules (line 2804): a `00` international
 * prefix is dropped, a leading `0` becomes `383`, bare 8/9-digit local numbers get `383` prefixed.
 */
export function waE164FromDigits(raw: string | null | undefined): string {
  let p = (raw ?? '').replace(/\D/g, '');
  if (p.startsWith('00')) p = p.slice(2);
  if (p.startsWith('0')) p = '383' + p.slice(1);
  if (p.length === 8 || p.length === 9) p = '383' + p;
  return p ? '+' + p : '';
}

/** +E.164 (or anything with digits) → digits-only for the `to` field. */
export function waDigitsFromPhone(phone: string): string {
  return waE164FromDigits(phone).replace(/\D/g, '');
}

const waTimestampSchema = z.union([z.string().regex(/^\d+$/), z.number().int().nonnegative()]);

/** Meta timestamps are epoch seconds (string). */
export function waTimestampToDate(ts: string | number): Date {
  return new Date(Number(ts) * 1000);
}

export const waErrorSchema = z
  .object({
    code: z.number(),
    title: z.string().optional(),
    message: z.string().optional(),
    error_data: z.object({ details: z.string() }).passthrough().optional(),
    href: z.string().optional(),
  })
  .passthrough();
export type WaError = z.infer<typeof waErrorSchema>;

const waMediaSchema = z
  .object({
    id: z.string().optional(),
    mime_type: z.string().optional(),
    sha256: z.string().optional(),
    caption: z.string().optional(),
    filename: z.string().optional(),
    animated: z.boolean().optional(),
    voice: z.boolean().optional(),
  })
  .passthrough();

export const waInboundMessageSchema = z
  .object({
    from: waDigitsSchema,
    id: z.string().min(1),
    timestamp: waTimestampSchema,
    type: z.string().min(1),
    text: z.object({ body: z.string() }).passthrough().optional(),
    image: waMediaSchema.optional(),
    document: waMediaSchema.optional(),
    audio: waMediaSchema.optional(),
    video: waMediaSchema.optional(),
    sticker: waMediaSchema.optional(),
    location: z
      .object({
        latitude: z.number(),
        longitude: z.number(),
        name: z.string().optional(),
        address: z.string().optional(),
      })
      .passthrough()
      .optional(),
    interactive: z
      .object({
        type: z.string(),
        button_reply: z.object({ id: z.string(), title: z.string() }).passthrough().optional(),
        list_reply: z
          .object({ id: z.string(), title: z.string(), description: z.string().optional() })
          .passthrough()
          .optional(),
      })
      .passthrough()
      .optional(),
    button: z.object({ text: z.string(), payload: z.string().optional() }).passthrough().optional(),
    reaction: z.object({ message_id: z.string(), emoji: z.string().optional() }).passthrough().optional(),
    context: z
      .object({ from: z.string().optional(), id: z.string().optional(), forwarded: z.boolean().optional() })
      .passthrough()
      .optional(),
    errors: z.array(waErrorSchema).optional(),
  })
  .passthrough();
export type WaRawInboundMessage = z.infer<typeof waInboundMessageSchema>;

export const waStatusSchema = z
  .object({
    id: z.string().min(1),
    status: z.string().min(1),
    timestamp: waTimestampSchema,
    recipient_id: z.string().optional(),
    conversation: z
      .object({
        id: z.string(),
        origin: z.object({ type: z.string() }).passthrough().optional(),
        expiration_timestamp: z.string().optional(),
      })
      .passthrough()
      .optional(),
    pricing: z.record(z.unknown()).optional(),
    errors: z.array(waErrorSchema).optional(),
  })
  .passthrough();
export type WaRawStatus = z.infer<typeof waStatusSchema>;

export const waContactSchema = z
  .object({
    profile: z.object({ name: z.string() }).passthrough().optional(),
    wa_id: z.string().min(1),
  })
  .passthrough();

export const waMetadataSchema = z
  .object({ display_phone_number: z.string(), phone_number_id: z.string() })
  .passthrough();

export const waChangeValueSchema = z
  .object({
    messaging_product: z.string().optional(),
    metadata: waMetadataSchema,
    contacts: z.array(waContactSchema).optional(),
    messages: z.array(waInboundMessageSchema).optional(),
    statuses: z.array(waStatusSchema).optional(),
    errors: z.array(waErrorSchema).optional(),
  })
  .passthrough();
export type WaChangeValue = z.infer<typeof waChangeValueSchema>;

export interface WaMessagesChange {
  field: 'messages';
  value: WaChangeValue;
}

export interface WaOtherChange {
  field: string;
  value: unknown;
}

export type WaChange = WaMessagesChange | WaOtherChange;

/**
 * `field: "messages"` must carry a valid conversation payload (rejected otherwise, never coerced);
 * other webhook fields (account_update, template status…) are accepted and ignored.
 */
export const waChangeSchema: z.ZodType<WaChange, z.ZodTypeDef, unknown> = z
  .object({ field: z.string(), value: z.unknown() })
  .passthrough()
  .transform((change, ctx): WaChange => {
    if (change.field !== 'messages') return { field: change.field, value: change.value };
    const parsed = waChangeValueSchema.safeParse(change.value);
    if (!parsed.success) {
      for (const issue of parsed.error.issues) ctx.addIssue({ ...issue, path: ['value', ...issue.path] });
      return z.NEVER;
    }
    return { field: 'messages', value: parsed.data };
  });

export function isMessagesChange(change: WaChange): change is WaMessagesChange {
  return change.field === 'messages';
}

export interface WaEntry {
  id: string;
  changes: WaChange[];
}

export const waEntrySchema: z.ZodType<WaEntry, z.ZodTypeDef, unknown> = z
  .object({ id: z.string(), changes: z.array(waChangeSchema) })
  .passthrough();

export interface WaWebhookPayload {
  object: 'whatsapp_business_account';
  entry: WaEntry[];
}

export const waWebhookPayloadSchema: z.ZodType<WaWebhookPayload, z.ZodTypeDef, unknown> = z
  .object({ object: z.literal('whatsapp_business_account'), entry: z.array(waEntrySchema) })
  .passthrough();

/** `GET /api/wa/webhook?hub.mode=subscribe&hub.verify_token=…&hub.challenge=…`. */
export const waWebhookVerifyQuerySchema = z.object({
  'hub.mode': z.literal('subscribe'),
  'hub.verify_token': z.string().min(1),
  'hub.challenge': z.string().min(1),
});
export type WaWebhookVerifyQuery = z.infer<typeof waWebhookVerifyQuerySchema>;

// ---- outbound ---------------------------------------------------------------------------------

export const WA_TEXT_MAX = 4096;

export const waSendTextRequestSchema = z.object({
  messaging_product: z.literal('whatsapp'),
  recipient_type: z.literal('individual').optional(),
  to: waDigitsSchema,
  type: z.literal('text'),
  text: z.object({ preview_url: z.boolean().optional(), body: z.string().min(1).max(WA_TEXT_MAX) }),
});
export type WaSendTextRequest = z.infer<typeof waSendTextRequestSchema>;

export const waTemplateParameterSchema = z.object({ type: z.literal('text'), text: z.string() });

export const waTemplateComponentSchema = z.object({
  type: z.enum(['header', 'body', 'button']),
  sub_type: z.string().optional(),
  index: z.union([z.string(), z.number()]).optional(),
  parameters: z.array(waTemplateParameterSchema),
});

export const waSendTemplateRequestSchema = z.object({
  messaging_product: z.literal('whatsapp'),
  recipient_type: z.literal('individual').optional(),
  to: waDigitsSchema,
  type: z.literal('template'),
  template: z.object({
    name: z.string().min(1),
    language: z.object({ code: z.string().min(1) }),
    components: z.array(waTemplateComponentSchema).optional(),
  }),
});
export type WaSendTemplateRequest = z.infer<typeof waSendTemplateRequestSchema>;

export const waSendRequestSchema = z.discriminatedUnion('type', [
  waSendTextRequestSchema,
  waSendTemplateRequestSchema,
]);
export type WaSendRequest = z.infer<typeof waSendRequestSchema>;

export const waSendResponseSchema = z
  .object({
    messaging_product: z.string().optional(),
    contacts: z.array(z.object({ input: z.string(), wa_id: z.string() }).passthrough()).optional(),
    messages: z
      .array(z.object({ id: z.string().min(1), message_status: z.string().optional() }).passthrough())
      .min(1),
  })
  .passthrough();
export type WaSendResponse = z.infer<typeof waSendResponseSchema>;

export const waApiErrorResponseSchema = z
  .object({
    error: z
      .object({
        message: z.string(),
        type: z.string().optional(),
        code: z.number(),
        error_subcode: z.number().optional(),
        error_data: z
          .object({ messaging_product: z.string().optional(), details: z.string() })
          .passthrough()
          .optional(),
        fbtrace_id: z.string().optional(),
      })
      .passthrough(),
  })
  .passthrough();
export type WaApiErrorResponse = z.infer<typeof waApiErrorResponseSchema>;

// ---- normalization ----------------------------------------------------------------------------

export interface WaInboundMessage {
  providerId: string;
  /** +E.164 */
  from: string;
  name: string;
  body: string;
  timestamp: Date;
  type: string;
  mediaId: string | null;
  raw: WaRawInboundMessage;
}

export interface WaInboundStatus {
  providerId: string;
  status: WaStatus;
  timestamp: Date;
  /** +E.164 or '' */
  recipient: string;
  errors: WaError[];
  raw: WaRawStatus;
}

export interface WaNormalizedInbound {
  phoneNumberId: string | null;
  messages: WaInboundMessage[];
  statuses: WaInboundStatus[];
}

function mediaOf(msg: WaRawInboundMessage): z.infer<typeof waMediaSchema> | undefined {
  return msg.image ?? msg.document ?? msg.audio ?? msg.video ?? msg.sticker;
}

/** Text body, caption, interactive reply title, button text or reaction emoji — else "[media]". */
export function waMessageBody(msg: WaRawInboundMessage): string {
  if (msg.type === 'text' && msg.text) return msg.text.body;
  if (msg.type === 'interactive' && msg.interactive) {
    const title = msg.interactive.button_reply?.title ?? msg.interactive.list_reply?.title;
    if (title) return title;
  }
  if (msg.type === 'button' && msg.button) return msg.button.text;
  if (msg.type === 'reaction' && msg.reaction?.emoji) return msg.reaction.emoji;
  const caption = mediaOf(msg)?.caption;
  return caption && caption.trim() !== '' ? caption : WA_MEDIA_PLACEHOLDER;
}

export function normalizeInbound(payload: WaWebhookPayload): WaNormalizedInbound {
  const messages: WaInboundMessage[] = [];
  const statuses: WaInboundStatus[] = [];
  let phoneNumberId: string | null = null;
  for (const entry of payload.entry) {
    for (const change of entry.changes) {
      if (!isMessagesChange(change)) continue;
      const value = change.value;
      phoneNumberId ??= value.metadata.phone_number_id;
      const names = new Map<string, string>();
      for (const c of value.contacts ?? []) {
        const n = c.profile?.name.trim();
        if (n) names.set(c.wa_id, n);
      }
      for (const m of value.messages ?? []) {
        messages.push({
          providerId: m.id,
          from: waE164FromDigits(m.from),
          name: names.get(m.from) ?? '',
          body: waMessageBody(m),
          timestamp: waTimestampToDate(m.timestamp),
          type: m.type,
          mediaId: mediaOf(m)?.id ?? null,
          raw: m,
        });
      }
      for (const s of value.statuses ?? []) {
        const status = waStatusValueSchema.safeParse(s.status);
        if (!status.success) continue;
        statuses.push({
          providerId: s.id,
          status: status.data,
          timestamp: waTimestampToDate(s.timestamp),
          recipient: s.recipient_id ? waE164FromDigits(s.recipient_id) : '',
          errors: s.errors ?? [],
          raw: s,
        });
      }
    }
  }
  return { phoneNumberId, messages, statuses };
}
